import React, { useEffect, useRef, useState } from 'react';
import EcosystemFrame, { EcosystemTicker } from '../three/EcosystemFrame';
import FocusDrawer from './FocusDrawer';
import './command.css';

// The one-screen stage: scene in the middle, HUD panels pinned to the
// edges, an activity rail along the bottom, and a focus drawer that slides
// in with the existing deep section for whatever was clicked. The grid is
// sized to the viewport minus the page chrome above it (`chrome` px) so the
// page never scrolls; the drawer scrolls internally.
//
//   panels: [{ id, title, side: 'left'|'right', level?, body, onOpen? }]
//   drawer: { [id]: { title, render: () => node } } — what each panel opens
export default function CommandView({
  data, derive, scene, panels = [], drawer = {}, events = [], caption = null, chrome = 176, className = '', onPick, children,
  telemetry = null,
}) {
  const simulated = telemetry?.source === 'sim';
  const [focus, setFocus] = useState(null);
  // The grid is viewport height minus whatever sits above it. Measure the
  // real offset (header rows, banners, help text vary per page) instead of
  // trusting the `chrome` estimate; re-measure on resize.
  const rootRef = useRef(null);
  const [top, setTop] = useState(null);
  useEffect(() => {
    const measure = () => { if (rootRef.current) setTop(Math.round(rootRef.current.getBoundingClientRect().top + window.scrollY)); };
    measure();
    const t = setTimeout(measure, 300);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [chrome, panels.length]);
  // Deep links (#ap-health …) open the matching drawer entry in command mode.
  useEffect(() => {
    const apply = () => { const id = window.location.hash.replace('#', ''); if (id && drawer[id]) setFocus(id); };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [drawer]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setFocus(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const left = panels.filter((p) => p.side !== 'right');
  const right = panels.filter((p) => p.side === 'right');
  const panel = (p) => (
    <button
      key={p.id}
      type="button"
      className={`cmd-panel is-${p.level || 'off'} ${p.wide ? 'is-wide' : ''}`}
      onClick={() => { if (p.onOpen) p.onOpen(); else if (drawer[p.id]) setFocus(p.id); }}
      aria-label={`Open ${p.title}`}
    >
      <span className="cmd-panel-title"><i className={`cmd-dot is-${p.level || 'off'}`} />{p.title}</span>
      <span className="cmd-panel-body">{p.body}</span>
    </button>
  );

  return (
    <div ref={rootRef} className={`cmd-view ${className} ${simulated ? 'is-simulated' : ''}`} style={{ '--cmd-chrome': `${(top ?? chrome) + 12}px` }}>
      <div className="cmd-stage">
        <EcosystemFrame data={data} derive={derive} scene={scene} fill caption={false} ticker={false} telemetry={telemetry} onPick={(id) => { if (onPick) onPick(id); else if (drawer[id]) setFocus(id); }} />
        {simulated && <div className="cmd-watermark" aria-label="Simulated data">SIMULATED{telemetry.scenario && telemetry.scenario !== 'calm' ? ` · ${telemetry.scenario}` : ''}</div>}
        {caption && <div className="cmd-caption">{simulated ? 'simulated · ' : ''}{caption}</div>}
      </div>
      <div className="cmd-col cmd-col-left">{left.map(panel)}</div>
      <div className="cmd-col cmd-col-right">{right.map(panel)}</div>
      <div className="cmd-rail">
        {children}
        <EcosystemTicker events={events} limit={5} />
      </div>
      <FocusDrawer open={!!focus} title={focus ? drawer[focus]?.title : ''} onClose={() => { setFocus(null); if (window.location.hash) window.history.replaceState(null, '', window.location.pathname + window.location.search); }}>
        {focus && drawer[focus]?.render?.()}
      </FocusDrawer>
    </div>
  );
}
